import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import api from '../utils/api';
import { Package, ArrowLeft, Printer } from 'lucide-react';
import { Button } from '../components/ui/button';

function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadOrder() {
      try {
        const response = await api.get(`/orders/${id}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Failed to load order', error);
      } finally {
        setLoading(false);
      }
    }
    
    loadOrder();
  }, [id]);

  function getStatusText(status) {
    const statusMap = {
      'new': t('status_new'),
      'eingegangen': t('status_received'),
      'in_bearbeitung': t('status_in_progress'),
      'versendet': t('status_shipped'),
      'geliefert': t('status_delivered')
    };
    return statusMap[status] || status;
  }

  function getStatusColor(status) {
    const colors = {
      'eingegangen': 'bg-blue-100 text-blue-800',
      'in_bearbeitung': 'bg-yellow-100 text-yellow-800',
      'versendet': 'bg-purple-100 text-purple-800',
      'geliefert': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  }

  function handlePrint() {
    window.print();
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 lg:px-12 py-8">
        <div className="text-center py-12">{t('loading')}</div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="container mx-auto px-4 lg:px-12 py-8">
        <div className="text-center py-12">{t('orderNotFound')}</div>
      </div>
    );
  }

  const LOCALE_MAP = { de: 'de-DE', en: 'en-GB', tr: 'tr-TR', ar: 'ar-SA', it: 'it-IT', fr: 'fr-FR', es: 'es-ES' };
  const orderNumber = order.id ? order.id.slice(0, 8).toUpperCase() : 'N/A';
  const orderDate = order.created_at ? new Date(order.created_at).toLocaleDateString(LOCALE_MAP[lang] || 'de-DE') : '';

  return (
    <div className="container mx-auto px-4 lg:px-12 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/orders')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('back')}
        </Button>
        
        <Button
          variant="outline"
          onClick={handlePrint}
          className="flex items-center gap-2 print:hidden"
        >
          <Printer className="h-4 w-4" />
          {t('print')}
        </Button>
      </div>

      {/* Order Detail Card */}
      <div className="bg-white border border-gray-200 rounded-md p-6 print:border-0 print:shadow-none">
        {/* Order Header */}
        <div className="flex items-center gap-3 mb-6 pb-6 border-b border-gray-200">
          <Package className="h-8 w-8" />
          <div>
            <h1 className="text-2xl font-heading font-bold">
              {t('order')} #{orderNumber}
            </h1>
            <p className="text-gray-500">{orderDate}</p>
          </div>
          <span className={`ms-auto px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
            {getStatusText(order.status)}
          </span>
        </div>

        {/* Payment Status */}
        <div className="mb-6">
          <h2 className="font-semibold mb-2">{t('paymentStatus')}</h2>
          {order.payment_status === 'paid' ? (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded text-sm font-medium">
              {t('paid')}
            </span>
          ) : order.payment_method === 'cash' ? (
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded text-sm font-medium">
              {t('payOnDelivery')}
            </span>
          ) : (
            <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded text-sm font-medium">
              {t('pending')}
            </span>
          )}
        </div>

        {/* Shipping Address */}
        {order.shipping_address && (
          <div className="mb-6">
            <h2 className="font-semibold mb-2">{t('shippingAddress')}</h2>
            <p className="text-gray-700 whitespace-pre-line">{order.shipping_address}</p>
          </div>
        )}

        {/* Order Items */}
        <div className="mb-6">
          <h2 className="font-semibold mb-4">{t('orderedItems')}</h2>
          <div className="space-y-3">
            {(order.items || []).map((item, idx) => (
              <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="font-medium">{item.product_name}</p>
                  <p className="text-sm text-gray-500">{t('quantity')}: {item.quantity}</p>
                </div>
                <p className="font-semibold">€{(item.total || 0).toFixed(2)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Order Summary */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">{t('subtotal')}</span>
            <span>€{(order.subtotal ?? ((order.total || 0) - (order.shipping_cost || 0))).toFixed(2)}</span>
          </div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-600">{t('shipping')}</span>
            <span>{(order.shipping_cost || 0) === 0 ? t('free') : `€${(order.shipping_cost || 0).toFixed(2)}`}</span>
          </div>
          <div className="flex justify-between items-center text-xl font-bold pt-2 border-t border-gray-200">
            <span>{t('total')}</span>
            <span>€{(order.total || 0).toFixed(2)}</span>
          </div>
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <h2 className="font-semibold mb-2">{t('notes')}</h2>
            <p className="text-gray-700">{order.notes}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default OrderDetailPage;
